import { mediaImageSet } from '@goalpost/content';
import { useTheme } from '../hooks/useTheme.tsx';

interface MediaImageProps {
  // Base media id without the color-scheme suffix or `-<width>.webp`, e.g.
  // `documents-software`. Resolves to the theme-matched `{base}-{light|dark}`
  // responsive WebP set through the media manifest.
  base: string;
  // Accessible description of what the screenshot shows.
  label: string;
  // `<img sizes>`: how wide the image renders at each breakpoint, so the browser
  // picks the right srcset rung. Defaults to the docs figure's display width.
  sizes?: string;
  // Class for the wrapping `<figure>`. Defaults to the docs figure frame; a
  // caller in a different layout (e.g. the home page's split) passes its own.
  className?: string;
}

// The docs figure sits in the article column (~720px content) and goes full-bleed
// on narrow screens. Callers with a different layout can override.
const DEFAULT_SIZES = '(min-width: 768px) 720px, 100vw';

// A theme-matched product screenshot resolved from the media manifest in
// `@goalpost/content`, rendered as a responsive `<img srcset>`. Like MediaVideo it
// renders nothing until the asset has been synced (the manifest is empty on a
// fresh checkout), so a page can embed an image before its bytes exist and light
// up automatically once `marketing:asset:push` runs.
export function MediaImage({
  base,
  label,
  sizes = DEFAULT_SIZES,
  className = 'mkt-doc-figure',
}: MediaImageProps) {
  const { theme } = useTheme();
  const preferred = theme === 'graphite' ? 'dark' : 'light';
  const fallback = preferred === 'dark' ? 'light' : 'dark';

  // Prefer the theme-matched set; fall back to the other color scheme so a partial
  // sync (only one uploaded) still shows the image rather than an empty gap. Both
  // are null until the manifest carries the rungs.
  const set =
    mediaImageSet(`${base}-${preferred}`) ?? mediaImageSet(`${base}-${fallback}`);
  if (!set) return null;

  return (
    <figure className={className}>
      <img
        src={set.src}
        srcSet={set.srcset}
        sizes={sizes}
        alt={label}
        loading="lazy"
        decoding="async"
      />
    </figure>
  );
}
